// @flow
import _ from 'lodash';
import ko from 'rgs-knockout';
import Q from 'q';
import {default as server, kladrRoute } from 'server2';
import type { DataResponse } from 'server2';

import AddressEditorModel from 'ko-component-address-editor';
import type {
	ExtendedAddress,
	StreetDetails,
	SettlementDetails,
} from 'calc-common';
import {
	ExtendedHousesDataSet,
	ExtendedSettlementsDataSet,
	ExtendedStreetsDataSet,
} from 'rgs-datasets2';
import ObservableMap from 'observable-map';
import stringify from 'json-stable-stringify';
import type { QPromise } from 'types/q';

export interface IExtendedAddressDictionaries {}

type InputOptions = {
	title: string,
	visible: boolean,
	disable: boolean,
};

type InputSettlementOptions = InputOptions & {
	events: {
		onKladrChanged?: ()=>void,
		idle?: ()=>void,
	},
};

export type ExtendedAddressEditorModelOptions = {
	address: ExtendedAddress,
	dictionaries: IExtendedAddressDictionaries,
	datasets: {
		settlements: ExtendedSettlementsDataSet,
		streetsForSettlement: ExtendedStreetsDataSet,
		houses: ExtendedHousesDataSet,
	},
	valueFilters: {
		settlements: (name: string) => boolean,
		streets: (name: string) => boolean,
		houses: (name: string, kladr: string) => boolean,
	},
	settlementNameProperty: string | 'Name',
	inputs?: {
		region: InputOptions,
		settlement: InputSettlementOptions,
		street: InputSettlementOptions,
		house: InputSettlementOptions,
		apartement: InputOptions,
		zipCode: InputOptions
	},
}

export default class ExtendedAddressEditorModel
	extends AddressEditorModel {

	constructor (options: ExtendedAddressEditorModelOptions) {
		super(options);

		this._cache = {
			streetsDetails: new Map,
			settlementsDetails: new Map,
		};

		this._options = _.defaultsDeep({}, this._options, {
			dictionaries: {
				kladr: {
					houses: new ObservableMap({ hash: (v) => stringify(v) }),
					streets: new ObservableMap({ hash: (v) => stringify(v) }),
					settlements: new ObservableMap({ hash: (v) => stringify(v) }),
				},
			},
			inputs: {
				region: {
					visible: true,
					disable: false,
				},
				settlement: {
					events: {
						onKladrChanged: _.noop,
					},
				},
				street: {
					events: {
						onKladrChanged: _.noop,
						idle: _.noop,
					},
				},
				house: {
					events: {
						onKladrChanged: _.noop,
						active: _.noop,
					},
				},
				apartement: {
					disable: false,
				},
				zipCode: {
					disable: false,
					visible: true,
				},
			},
		});

		_.merge(this._options, {
			inputs: {
				street: {
					events: {
						idle: (...args) => {
							const outerHandler = _.get(options, 'inputs.street.events.idle');

							this.checkHousesExistsHandler();
							this.checkSettlementHasStreetsOrHousesHandler();
							_.isFunction(outerHandler) && outerHandler(args);
						},
					},
				},
				house: {
					events: {
						active: (...args) => {
							const outerHandler = _.get(options, 'inputs.house.events.active');

							this.checkHousesExistsHandler();
							this.checkSettlementHasStreetsOrHousesHandler();
							_.isFunction(outerHandler) && outerHandler(args);
						},
					},
				},
			},
		});

		_.merge(this._inputs, {
			region: {
				title: this._options.inputs.region.title,
				visible: this._options.inputs.region.visible,
				disable: ko.pureComputed(() => {
					return ko.unwrap(this._options.disable)
						|| ko.unwrap(this._options.inputs.region.disable);
				}),
			},
			settlement: {
				title: this._options.inputs.settlement.title,
				events: this._options.inputs.settlement.events,
			},
			street: {
				title: this._options.inputs.street.title,
				events: this._options.inputs.street.events,
			},
			house: {
				title: this._options.inputs.house.title,
				events: this._options.inputs.house.events,
			},
			zipCode: {
				title: this._options.inputs.zipCode.title,
				visible: ko.pureComputed(() => {
					return ko.unwrap(this._options.inputs.zipCode.visible);
				}),
				disable: ko.pureComputed(() => {
					return ko.unwrap(this._options.disable)
						|| ko.unwrap(this._options.inputs.zipCode.disable);
				}),
			},
			apartement: {
				title: this._options.inputs.apartement.title,
				disable: ko.pureComputed(() => {
					return ko.unwrap(this._options.disable)
						|| ko.unwrap(this._options.inputs.apartement.disable);
				}),
			},
		});

		if (ko.isObservable(options.address.RegionID)) {
			options.address.RegionID.subscribe(() => {
				this._clearTypeaheadRemoteCache(this._options.datasets.settlements);
			});
		}

		options.address.Settlement.Kladr.subscribe(() => {
			this._clearTypeaheadRemoteCache(this._options.datasets.streetsForSettlement);
		});

		options.address.Street.Kladr.subscribe(() => {
			this._clearTypeaheadRemoteCache(this._options.datasets.houses);
		});
	}

	_clearTypeaheadRemoteCache (dataset) {
		if (dataset) {
			dataset().source.clearRemoteCache();
		}
	}

	_streetHasHouses (streetKladr: string): QPromise<boolean> {
		if (this._cache.streetsDetails.has(streetKladr)) {
			return Q.resolve(this._cache.streetsDetails.get(streetKladr));
		}

		return server.any.rest.get(
			[
				kladrRoute.prefix,
				kladrRoute.extended,
				kladrRoute.streets,
				streetKladr,
			]
		)
			.then(({ responseJSON: { Data: { HasHouses } } }: DataResponse<StreetDetails> ) => {
				this._cache.streetsDetails.set(streetKladr, HasHouses);
				return HasHouses;
			});
	}

	_settlementHasStreetsOrHouses (settlementKladr: string): QPromise<{
			hasStreets: boolean,
			hasHouses: boolean,
		}> {
		if (this._cache.settlementsDetails.has(settlementKladr)) {
			return Q.resolve(this._cache.settlementsDetails.get(settlementKladr));
		}

		return server.any.rest.get([
			kladrRoute.prefix,
			kladrRoute.extended,
			kladrRoute.settlements,
			settlementKladr,
		])
			.then(({ responseJSON: { Data: {
				HasHouses,
				HasStreets,
			} } }: DataResponse<SettlementDetails>) => {
				const result = {
					hasHouses: HasHouses,
					hasStreets: HasStreets,
				};
				this._cache.settlementsDetails.set(settlementKladr, result);
				return result;
			});
	}

	async checkHousesExistsHandler () {
		const streetKladr = this._address.Street.Kladr();

		if (!streetKladr) {
			return;
		}

		const hasHouses = await this._streetHasHouses(streetKladr);

		this._address.Street.hasHouses(hasHouses);
	}

	async checkSettlementHasStreetsOrHousesHandler () {
		const settlementKladr = this._address.Settlement.Kladr();

		if (!settlementKladr) {
			return;
		}

		const { hasStreets, hasHouses } = await this._settlementHasStreetsOrHouses(settlementKladr);

		this._address.Settlement.hasStreets(hasStreets);
		this._address.Settlement.hasHouses(hasHouses);
	}

	settlementTypeaheadSelect (model, e, option) {
		/*
		 Важно чтобы Name был перезаписан после CityName и AreaName, т.к.
		 поиск Name в словаре базируется на  CityName и AreaName
		*/
		const { Name, CityName, AreaName } = option;
		const settlement = this._address.Settlement;
		settlement.CityName(CityName);
		settlement.AreaName(AreaName);
		settlement.Name(Name);

		this._inputs.settlement.events.onKladrChanged(this._address);
	}
}
